import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Video, Loader2, Upload, ImageIcon, Plus, Trash2, Eye, GripVertical, Play, FolderOpen, X, Calendar, Clock } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import BuilderTopBar from '@/components/creation-flow/BuilderTopBar';
import { BuilderCard, builderLabelStyle, builderErrorStyle, builderHelpStyle } from '@/components/creation-flow/BuilderCard';
import { UploadProgress } from '@/components/creation-flow/UploadProgress';
import {
  createProduct, updateProduct, fetchProductForEdit, getProductFileUploadUrl, requestMuxIngest,
  type CreatorProduct, type ProductVideo, type MuxPreview,
} from '@/services/creatorApi';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import CategoryTagsFields from '@/components/creation-flow/CategoryTagsFields';
import IncludedProductsPicker from '@/components/creation-flow/IncludedProductsPicker';
import RosterCard from '@/components/creation-flow/RosterCard';
import MakeInStudioLink from '@/components/creation-flow/MakeInStudioLink';
import VideoEditDrawer from '@/components/video-editor/VideoEditDrawer';

interface ProjectFile { id: string; name: string; s3Key?: string; duration?: number; thumbnailUrl?: string; }
interface PrefillState { title?: string; description?: string; priceCents?: number; }
type Format = 'single' | 'series';

const VideoBuilderPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { getAuthHeaders } = useAuth();
  const isEditing = !!productId;
  const prefill = (location.state || {}) as PrefillState;

  const [format, setFormat] = useState<Format>('single');
  const [title, setTitle] = useState(prefill.title || '');
  const [description, setDescription] = useState(prefill.description || '');
  const [priceInput, setPriceInput] = useState(prefill.priceCents != null ? (prefill.priceCents / 100).toFixed(2) : '9.99');
  const [pricingMode, setPricingMode] = useState<'one_time' | 'subscription'>('one_time');
  const [includedProductIds, setIncludedProductIds] = useState<string[]>([]);
  const [coverImage, setCoverImage] = useState('');
  const [published, setPublished] = useState(true);
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [videos, setVideos] = useState<ProductVideo[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Preview clip (short public Mux asset).
  const [previewClipS3Key, setPreviewClipS3Key] = useState<string | null>(null);
  const [previewClipName, setPreviewClipName] = useState<string | null>(null);
  const [uploadingPreviewClip, setUploadingPreviewClip] = useState(false);
  const [previewPercent, setPreviewPercent] = useState<number | null>(null);
  const [existingMuxPreview, setExistingMuxPreview] = useState<MuxPreview | null>(null);

  // Project picker
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const [dirty, setDirty] = useState(false);
  const initialLoadRef = useRef(true);

  // Inline video edit drawer — one instance for the page; index of the entry
  // being edited. On apply, the finalized patch lands on that ProductVideo
  // (save-time Mux fan-out covers ingest if the patch lacks an asset).
  const [editVideoIdx, setEditVideoIdx] = useState<number | null>(null);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    fetchProductForEdit(productId)
      .then(p => {
        if (!p) { toast({ title: 'Video not found', variant: 'destructive' }); navigate('/my-store'); return; }
        setTitle(p.title);
        setDescription(p.description);
        setPriceInput((p.price / 100).toFixed(2));
        setPricingMode(p.pricingMode || 'one_time');
        setIncludedProductIds(p.includedProductIds || []);
        setCoverImage(p.coverImage || '');
        setPublished(p.published);
        setCategory((p as any).category || '');
        setTags((p as any).tags || []);
        setVideos(p.videos || []);
        setExistingMuxPreview(p.muxPreview || null);
        // Prefer the persisted format; fall back to the legacy >1-video heuristic
        // for products saved before the format field existed.
        setFormat(p.format || ((p.videos?.length || 0) > 1 ? 'series' : 'single'));
      })
      .catch(err => toast({ title: 'Error', description: err.message, variant: 'destructive' }))
      .finally(() => { setLoading(false); initialLoadRef.current = true; setDirty(false); });
  }, [productId]);

  useEffect(() => {
    if (initialLoadRef.current) { initialLoadRef.current = false; return; }
    setDirty(true);
  }, [format, title, description, priceInput, pricingMode, coverImage, published, videos]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const loadProjectFiles = useCallback(async () => {
    if (projectFiles.length > 0) return;
    setLoadingProjects(true);
    try {
      const res = await fetch('/api/projects', { headers: getAuthHeaders() });
      if (res.ok) {
        const { projects } = await res.json();
        const files: ProjectFile[] = [];
        for (const proj of projects || []) {
          for (const f of proj.files || []) {
            if (f.fileType === 'video' && f.s3Key) files.push({ id: f.id || f.s3Key, name: f.name || 'Untitled', s3Key: f.s3Key, duration: f.duration, thumbnailUrl: f.thumbnailUrl });
          }
          for (const r of proj.renders || []) {
            if (r.savedS3Key) files.push({ id: r.id || r.savedS3Key, name: r.name || 'Rendered Video', s3Key: r.savedS3Key, duration: r.duration, thumbnailUrl: r.thumbnailUrl });
          }
        }
        setProjectFiles(files);
      }
    } catch { /* non-fatal */ } finally { setLoadingProjects(false); }
  }, [projectFiles.length, getAuthHeaders]);

  const openPicker = () => { setShowPicker(true); loadProjectFiles(); };

  const handleBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    navigate('/my-store');
  };

  // --- video ops ---
  const addVideoFromProject = (f: ProjectFile) => {
    if (videos.some(v => v.s3Key === f.s3Key)) return;
    const next: ProductVideo = { title: f.name, s3Key: f.s3Key!, duration: f.duration, thumbnailUrl: f.thumbnailUrl, fileType: 'video' };
    setVideos(prev => format === 'single' ? [next] : [...prev, next]);
    if (format === 'single') setShowPicker(false);
  };
  const removeVideo = (i: number) => setVideos(prev => prev.filter((_, idx) => idx !== i));
  // Drawer apply: swap in the edited clip + fresh Mux fields; the old thumbnail
  // and duration belong to the previous clip, so drop them.
  const applyVideoEdit = (i: number, patch: { s3Key: string; muxAssetId?: string; muxPlaybackId?: string; muxIngestStatus?: ProductVideo['muxIngestStatus'] }) =>
    setVideos(prev => prev.map((v, idx) => idx === i
      ? { ...v, s3Key: patch.s3Key, muxAssetId: patch.muxAssetId, muxPlaybackId: patch.muxPlaybackId, muxIngestStatus: patch.muxIngestStatus, thumbnailUrl: undefined, duration: undefined }
      : v));
  const updateVideoTitle = (i: number, t: string) => setVideos(prev => prev.map((v, idx) => idx === i ? { ...v, title: t } : v));
  const toggleFreePreview = (i: number) => setVideos(prev => prev.map((v, idx) => idx === i ? { ...v, freePreview: !v.freePreview } : v));
  const updateVideoLogline = (i: number, t: string) => setVideos(prev => prev.map((v, idx) => idx === i ? { ...v, logline: t } : v));
  // Release control: 'immediate' (no gate), 'day' (N days after access), 'date' (absolute).
  const updateVideoRelease = (i: number, release: { mode: 'immediate' | 'day' | 'date'; value?: number | string }) =>
    setVideos(prev => prev.map((v, idx) => {
      if (idx !== i) return v;
      if (release.mode === 'immediate') return { ...v, dripDay: 0, availableDate: undefined };
      if (release.mode === 'day') return { ...v, dripDay: Math.max(0, Math.round(Number(release.value) || 0)), availableDate: undefined };
      return { ...v, dripDay: 0, availableDate: (release.value as string) || undefined };
    }));
  // "Release weekly": episode 1 immediate, then +7 days each, in current order.
  const applyWeeklyDrip = () => setVideos(prev => prev.map((v, idx) => ({ ...v, dripDay: idx * 7, availableDate: undefined })));
  const reorder = (result: DropResult) => {
    if (!result.destination) return;
    setVideos(prev => {
      const items = Array.from(prev);
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination!.index, 0, moved);
      return items;
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024 * 1024) { toast({ title: 'File too large', description: 'Max file size is 500MB.', variant: 'destructive' }); return; }
    setUploadingFile(true); setUploadProgress(0);
    try {
      const { uploadUrl, key, fileType: detectedType } = await getProductFileUploadUrl(file.name, file.type);
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', ev => { if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100)); });
        xhr.addEventListener('load', () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed')));
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
      const next: ProductVideo = { title: file.name, s3Key: key, fileType: detectedType as ProductVideo['fileType'], fileSize: file.size };
      setVideos(prev => format === 'single' ? [next] : [...prev, next]);
      toast({ title: 'File uploaded' });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingFile(false); setUploadProgress(0); e.target.value = ''; }
  };

  const handlePreviewClipUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) { toast({ title: 'Preview clip must be a video file', variant: 'destructive' }); return; }
    if (file.size > 1024 * 1024 * 1024) { toast({ title: 'Preview clip too large', description: 'Max 1GB.', variant: 'destructive' }); return; }
    setUploadingPreviewClip(true); setPreviewPercent(0);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(`preview-${file.name}`, file.type);
      await uploadToSignedUrl(uploadUrl, file, setPreviewPercent);
      setPreviewClipS3Key(key); setPreviewClipName(file.name); setExistingMuxPreview(null);
      toast({ title: 'Preview clip uploaded — save to finalize' });
    } catch (err: any) {
      toast({ title: 'Preview clip upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingPreviewClip(false); setPreviewPercent(null); e.target.value = ''; }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast({ title: 'Please select an image file', variant: 'destructive' }); return; }
    if (file.size > 5 * 1024 * 1024) { toast({ title: 'Image must be under 5MB', variant: 'destructive' }); return; }
    setUploadingCover(true);
    try {
      const { uploadUrl, key } = await getProductFileUploadUrl(`cover-${file.name}`, file.type);
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!putRes.ok) throw new Error('Upload failed');
      setCoverImage(key);
      toast({ title: 'Cover image uploaded' });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingCover(false); e.target.value = ''; }
  };

  const handleSave = async () => {
    const errs: Record<string, string> = {};
    if (!title.trim()) errs.title = 'Title is required';
    const priceCents = Math.round(parseFloat(priceInput) * 100);
    if (isNaN(priceCents) || priceCents < 0 || (priceCents > 0 && priceCents < 100)) errs.price = 'Enter $0 for free, or at least $1.00';
    const outVideos = format === 'single' ? videos.slice(0, 1) : videos;
    if (outVideos.length === 0) errs.videos = 'Add at least one video or file';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      const payload: Record<string, any> = {
        title: title.trim(),
        description: description.trim(),
        price: priceCents,
        // Commerce stays uniform (productType 'single'); format carries the
        // single-vs-series distinction. Episode order = array order.
        productType: 'single',
        format,
        coverImage: coverImage || undefined,
        published,
        pricingMode,
        videos: outVideos,
        category: category || null,
        tags,
      };
      if (pricingMode === 'subscription') payload.subscriptionInterval = 'month';
      payload.includedProductIds = pricingMode === 'subscription' ? includedProductIds : [];
      const saved = isEditing ? await updateProduct(productId!, payload) : await createProduct(payload);

      // Mux ingest fan-out — per video + preview not already provisioned.
      if (saved && saved._id) {
        const ingestPromises: Promise<unknown>[] = [];
        (saved.videos || []).forEach((v, idx) => {
          if (v.fileType && v.fileType !== 'video') return;
          if (v.muxAssetId) return;
          ingestPromises.push(requestMuxIngest({ productId: saved._id, videoIndex: idx, s3Key: v.s3Key, kind: 'paid' }).catch(err => console.warn('[mux] paid ingest failed', idx, err)));
        });
        if (previewClipS3Key && !saved.muxPreview) {
          ingestPromises.push(requestMuxIngest({ productId: saved._id, s3Key: previewClipS3Key, kind: 'preview' }).catch(err => console.warn('[mux] preview ingest failed', err)));
        }
        if (ingestPromises.length) {
          await Promise.race([Promise.allSettled(ingestPromises), new Promise(resolve => setTimeout(resolve, 4000))]);
        }
      }

      setDirty(false);
      if (isEditing) {
        toast({ title: 'Saved' });
      } else {
        toast({ title: 'Created' });
        navigate(`/my-store/video/${saved._id}`, { replace: true });
      }
    } catch (err: any) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center" data-paper="beige" style={{ background: 'var(--hj-beige)' }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-beige-ink-2)' }} />
      </main>
    );
  }

  const coverSrc = coverImage ? (coverImage.startsWith('http') || coverImage.startsWith('/api/') ? coverImage : `/api/s3/${coverImage}`) : null;
  const totalMin = Math.round(videos.reduce((s, v) => s + (v.duration || 0), 0) / 60);
  const meta = format === 'series'
    ? `Series · ${videos.length} episode${videos.length !== 1 ? 's' : ''}${totalMin ? ` · ${totalMin} min` : ''}`
    : 'Single video';
  const shownVideos = format === 'single' ? videos.slice(0, 1) : videos;
  const canAdd = format === 'series' || shownVideos.length === 0;
  const isSeries = format === 'series';
  const hasDrip = videos.some(v => (v.dripDay || 0) > 0 || !!v.availableDate);

  const thumbFor = (v: ProductVideo) => v.thumbnailUrl ? (v.thumbnailUrl.startsWith('http') ? v.thumbnailUrl : `/api/s3/${v.thumbnailUrl}`) : null;

  // --hj-bg remap keeps the sticky BuilderTopBar on paper too — hj-event-paper
  // flips the ink vars dark but not the bg, which read black-on-black.
  return (
    <main className="min-h-screen hj-event-paper" data-paper="beige" style={{ background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', ['--hj-bg' as any]: 'var(--hj-beige)', ['--hj-surface-1' as any]: '#fff', ['--hj-surface-2' as any]: 'var(--hj-beige-hover)' }}>
      <BuilderTopBar
        kicker={isEditing ? 'Edit video / series' : 'New video / series'}
        kickerIcon={Video}
        kickerColor="var(--hj-deep-blue)"
        title={title}
        titlePlaceholder="Untitled"
        meta={meta}
        onBack={handleBack}
        saving={saving}
        dirty={dirty}
        saveLabel={isEditing ? 'Save changes' : 'Publish'}
        onSave={handleSave}
        previewHref={isEditing ? `/course/${productId}` : undefined}
      />

      <div className="mx-auto" style={{ maxWidth: 1120, padding: '24px 16px 96px' }}>
        <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
          {/* Left rail: settings */}
          <div className="w-full lg:w-80 lg:flex-shrink-0 lg:sticky flex flex-col gap-5" style={{ top: 88 }}>
            <BuilderCard kicker="Settings">
              {/* Single / Series segmented toggle */}
              <div>
                <Label style={builderLabelStyle}>Format</Label>
                <div className="grid grid-cols-2 gap-1" style={{ padding: 4, borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-beige-deep)' }}>
                  {(['single', 'series'] as Format[]).map(f => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFormat(f)}
                      style={{
                        padding: '7px 10px',
                        borderRadius: 8,
                        border: 0,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 13,
                        fontWeight: format === f ? 700 : 500,
                        background: format === f ? '#fff' : 'transparent',
                        color: 'var(--hj-beige-ink)',
                        boxShadow: format === f ? '0 1px 2px rgb(0 0 0 / 0.08)' : 'none',
                      }}
                    >
                      {f === 'single' ? 'Single' : 'Series'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cover */}
              <div>
                <Label style={builderLabelStyle}>Cover image</Label>
                <div className="w-full flex items-center justify-center overflow-hidden" style={{ aspectRatio: '16 / 10', borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-beige-hover)', border: '1px dashed var(--hj-beige-line)' }}>
                  {coverSrc ? <img src={coverSrc} alt="Cover" className="w-full h-full" style={{ objectFit: 'cover' }} /> : (
                    <div className="text-center" style={{ color: 'var(--hj-beige-ink-3)' }}><ImageIcon className="w-6 h-6 mx-auto" /><div style={{ fontSize: 11.5, marginTop: 4 }}>Upload cover</div></div>
                  )}
                </div>
                <label className="cursor-pointer">
                  <span className="inline-flex items-center gap-1.5 w-full justify-center" style={{ marginTop: 8, padding: '8px 14px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600, minHeight: 38 }}>
                    {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {coverImage ? 'Change cover' : 'Upload cover'}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={uploadingCover} />
                </label>
                {format !== 'series' && (
                  <div style={{ marginTop: 8 }}>
                    <MakeInStudioLink output="card" headline={title || undefined} coverProductId={isEditing ? productId : undefined} />
                  </div>
                )}
                {/* Series get a vertical 9:16 poster path in the Image Studio. */}
                {format === 'series' && (
                  <button
                    type="button"
                    onClick={() => {
                      const params = new URLSearchParams({ output: 'poster' });
                      if (title.trim()) params.set('headline', title.trim());
                      if (productId) params.set('cover', productId);
                      navigate(`/my-store/studio?${params.toString()}`);
                    }}
                    className="inline-flex items-center gap-1.5 w-full justify-center"
                    style={{ marginTop: 8, padding: '8px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12.5, fontWeight: 600, minHeight: 38, cursor: 'pointer' }}
                  >
                    <ImageIcon className="w-4 h-4" /> Make a poster
                  </button>
                )}
              </div>

              <div>
                <Label style={builderLabelStyle}>Title</Label>
                <Input value={title} onChange={e => { setTitle(e.target.value); setErrors(x => ({ ...x, title: '' })); }} placeholder="e.g. Reel Season 1" />
                {errors.title && <p style={builderErrorStyle}>{errors.title}</p>}
              </div>
              <div>
                <Label style={builderLabelStyle}>Description</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="What's this about?" />
              </div>
              <CategoryTagsFields category={category} setCategory={setCategory} tags={tags} setTags={setTags} />
              <div>
                <Label style={builderLabelStyle}>Price (USD){pricingMode === 'subscription' ? ' /mo' : ''}</Label>
                <Input type="number" min="0" step="0.01" value={priceInput} onChange={e => { setPriceInput(e.target.value); setErrors(x => ({ ...x, price: '' })); }} placeholder="9.99" />
                {errors.price && <p style={builderErrorStyle}>{errors.price}</p>}
              </div>
              <div>
                <Label style={builderLabelStyle}>Pricing mode</Label>
                <Select value={pricingMode} onValueChange={(v: 'one_time' | 'subscription') => setPricingMode(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One-time purchase</SelectItem>
                    <SelectItem value="subscription">Monthly subscription</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {pricingMode === 'subscription' && (
                <IncludedProductsPicker value={includedProductIds} onChange={setIncludedProductIds} excludeId={productId} />
              )}
              <div className="flex items-center justify-between">
                <div>
                  <Label style={{ ...builderLabelStyle, marginBottom: 2 }}>Published</Label>
                  <p className="m-0" style={{ fontSize: 12, color: 'var(--hj-beige-ink-2)' }}>Visible in your store.</p>
                </div>
                <Switch checked={published} onCheckedChange={setPublished} />
              </div>
            </BuilderCard>

            {/* Preview clip */}
            <BuilderCard kicker="Preview clip">
              <p className="m-0" style={builderHelpStyle}>Optional teaser shown on your public store page. A minute or two holds attention best, but any length up to 1GB works.</p>
              <UploadProgress percent={uploadingPreviewClip ? previewPercent : null} label="Uploading clip…" />
              <label className="cursor-pointer">
                <span className="inline-flex items-center gap-1.5 w-full justify-center" style={{ padding: '8px 14px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600, minHeight: 38 }}>
                  {uploadingPreviewClip ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {(previewClipS3Key || existingMuxPreview) ? 'Replace clip' : 'Upload clip'}
                </span>
                <input type="file" accept="video/*" className="hidden" onChange={handlePreviewClipUpload} disabled={uploadingPreviewClip} />
              </label>
              {previewClipS3Key ? (
                <p className="m-0" style={builderHelpStyle}>Staged: <strong>{previewClipName}</strong> — saves on submit.</p>
              ) : existingMuxPreview ? (
                <p className="m-0" style={builderHelpStyle}>Active ({existingMuxPreview.muxIngestStatus === 'ready' ? 'live' : existingMuxPreview.muxIngestStatus})</p>
              ) : null}
            </BuilderCard>

            {/* Who bought it — edit mode only. */}
            {isEditing && <RosterCard productId={productId} kicker="Buyers" noun="purchased" fileLabel="buyers" title={title} />}
          </div>

          {/* Right: episodes */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <h2 className="font-hj-display m-0" style={{ fontSize: 20, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
                {format === 'series' ? 'Episodes' : 'Video'}
              </h2>
              {canAdd && (
                <div className="flex gap-2">
                  <button type="button" onClick={openPicker} className="inline-flex items-center gap-1.5" style={{ padding: '7px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', minHeight: 36 }}>
                    <FolderOpen className="w-3.5 h-3.5" /> From projects
                  </button>
                  <label className="cursor-pointer inline-flex items-center gap-1.5" style={{ padding: '7px 14px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 12.5, fontWeight: 700, minHeight: 36 }}>
                    {uploadingFile ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {uploadProgress}%</> : <><Upload className="w-3.5 h-3.5" /> Upload</>}
                    <input type="file" accept=".pdf,.zip,.png,.jpg,.jpeg,.gif,.mp4,.mov,.mp3,.wav" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
                  </label>
                </div>
              )}
            </div>
            {errors.videos && <p style={{ ...builderErrorStyle, marginBottom: 10 }}>{errors.videos}</p>}

            {/* Series release toolbar — schedule a weekly drip in one tap. */}
            {isSeries && videos.length > 1 && (
              <div className="flex items-center flex-wrap gap-x-3 gap-y-2" style={{ marginBottom: 12 }}>
                <button type="button" onClick={applyWeeklyDrip} className="inline-flex items-center gap-1.5" style={{ padding: '6px 12px', borderRadius: 999, background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <Calendar className="w-3.5 h-3.5" /> Release weekly
                </button>
                <span style={{ fontSize: 12, color: 'var(--hj-ink-3)' }}>Episode 1 unlocks on purchase, then one per week.</span>
              </div>
            )}

            {/* Subscription nudge — drip pairs best with a subscription (nudge, not force). */}
            {isSeries && hasDrip && pricingMode === 'one_time' && (
              <div className="flex items-center justify-between gap-3" style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 'var(--hj-r-sm)', background: 'rgb(65 113 249 / 0.08)', border: '1px solid rgb(65 113 249 / 0.25)' }}>
                <span style={{ fontSize: 12.5, color: 'var(--hj-ink-2)' }}>Timed releases work best as a monthly subscription — fans stay for the next drop.</span>
                <button type="button" onClick={() => setPricingMode('subscription')} className="flex-shrink-0" style={{ padding: '6px 12px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Use subscription
                </button>
              </div>
            )}

            {/* Project picker */}
            {showPicker && (
              <BuilderCard style={{ marginBottom: 14 }}>
                <div className="flex items-center justify-between">
                  <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)' }}>Pull from your Hey Joe projects</div>
                  <button type="button" onClick={() => setShowPicker(false)} style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--hj-beige-ink-3)' }}><X className="w-4 h-4" /></button>
                </div>
                <div className="overflow-y-auto flex flex-col gap-1" style={{ maxHeight: 240 }}>
                  {loadingProjects ? (
                    <div className="flex items-center gap-2 py-4 justify-center" style={{ color: 'var(--hj-beige-ink-3)' }}><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                  ) : projectFiles.length === 0 ? (
                    <p className="text-center py-4 m-0" style={{ fontSize: 13, color: 'var(--hj-beige-ink-3)' }}>No videos found in your projects.</p>
                  ) : projectFiles.map(f => {
                    const added = videos.some(v => v.s3Key === f.s3Key);
                    return (
                      <button key={f.id} type="button" disabled={added} onClick={() => addVideoFromProject(f)}
                        className="w-full flex items-center gap-2 text-left"
                        style={{ padding: 8, borderRadius: 8, background: added ? 'transparent' : '#fff', border: '1px solid var(--hj-beige-line)', opacity: added ? 0.5 : 1, cursor: added ? 'default' : 'pointer' }}>
                        <Video className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-beige-ink-3)' }} />
                        <span className="flex-1 truncate" style={{ fontSize: 13, color: 'var(--hj-beige-ink)' }}>{f.name}</span>
                        {added ? <Badge variant="outline" className="text-[10px]">Added</Badge> : <Plus className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-3)' }} />}
                      </button>
                    );
                  })}
                </div>
              </BuilderCard>
            )}

            {shownVideos.length === 0 ? (
              <div className="text-center" style={{ padding: '56px 24px', borderRadius: 'var(--hj-r-md)', border: '1.5px dashed var(--hj-beige-line)', background: '#fff' }}>
                <p className="m-0" style={{ fontSize: 14, color: 'var(--hj-beige-ink-3)', marginBottom: 14 }}>
                  {format === 'series'
                    ? 'Build your series episode by episode — pull takes from your Hey Joe projects or upload finished video.'
                    : 'Your video goes here — pull it from your Hey Joe projects or upload a finished file.'}
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <button type="button" onClick={openPicker} className="inline-flex items-center gap-1.5" style={{ padding: '9px 18px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', minHeight: 40 }}>
                    <FolderOpen className="w-3.5 h-3.5" /> From your projects
                  </button>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer" style={{ padding: '9px 18px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', minHeight: 40, alignItems: 'center', display: 'inline-flex' }}>
                    <Upload className="w-3.5 h-3.5" /> Upload video
                    <input type="file" accept=".mp4,.mov,.mp3,.wav" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
                  </label>
                </div>
              </div>
            ) : format === 'single' ? (
              <EpisodeRow v={shownVideos[0]} index={0} draggable={false} isSeries={false} thumb={thumbFor(shownVideos[0])} onTitle={updateVideoTitle} onToggleFree={toggleFreePreview} onLogline={updateVideoLogline} onRelease={updateVideoRelease} onRemove={removeVideo} onEditVideo={setEditVideoIdx} />
            ) : (
              <DragDropContext onDragEnd={reorder}>
                <Droppable droppableId="episodes">
                  {provided => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col gap-2">
                      {videos.map((v, i) => (
                        <Draggable key={v.s3Key || i} draggableId={v.s3Key || String(i)} index={i}>
                          {prov => (
                            <div ref={prov.innerRef} {...prov.draggableProps}>
                              <EpisodeRow v={v} index={i} draggable isSeries thumb={thumbFor(v)} dragHandleProps={prov.dragHandleProps} onTitle={updateVideoTitle} onToggleFree={toggleFreePreview} onLogline={updateVideoLogline} onRelease={updateVideoRelease} onRemove={removeVideo} onEditVideo={setEditVideoIdx} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>
      </div>

      {/* Inline video edit drawer (single page-level instance) */}
      {editVideoIdx !== null && !!videos[editVideoIdx]?.s3Key && (
        <VideoEditDrawer
          open
          video={{ s3Key: videos[editVideoIdx].s3Key, title: videos[editVideoIdx].title || undefined, muxPlaybackId: videos[editVideoIdx].muxPlaybackId }}
          onApplied={(patch) => applyVideoEdit(editVideoIdx, patch)}
          onClose={() => setEditVideoIdx(null)}
        />
      )}
    </main>
  );
};

// A single episode / video row (beige card). Reused for single mode (no drag/index).
// In series mode a second line adds an optional logline and a release control.
function EpisodeRow({
  v, index, draggable, isSeries, thumb, dragHandleProps, onTitle, onToggleFree, onLogline, onRelease, onRemove, onEditVideo,
}: {
  v: ProductVideo; index: number; draggable: boolean; isSeries: boolean; thumb: string | null;
  dragHandleProps?: any;
  onTitle: (i: number, t: string) => void;
  onToggleFree: (i: number) => void;
  onLogline: (i: number, t: string) => void;
  onRelease: (i: number, release: { mode: 'immediate' | 'day' | 'date'; value?: number | string }) => void;
  onRemove: (i: number) => void;
  /** Opens the inline video edit drawer for this entry. */
  onEditVideo?: (i: number) => void;
}) {
  const isVideo = !v.fileType || v.fileType === 'video';
  const releaseMode: 'immediate' | 'day' | 'date' = v.availableDate ? 'date' : ((v.dripDay || 0) > 0 ? 'day' : 'immediate');
  return (
    <div data-paper="beige" style={{ padding: 12, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
      <div className="flex items-center gap-3">
        {draggable && (
          <span {...dragHandleProps} className="flex-shrink-0 cursor-grab" style={{ color: 'var(--hj-beige-ink-3)' }} aria-label="Drag to reorder"><GripVertical className="w-4 h-4" /></span>
        )}
        {draggable && <span className="tabular flex-shrink-0 text-center" style={{ width: 20, fontSize: 13, color: 'var(--hj-beige-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>{index + 1}</span>}
        <div className="flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ width: 64, height: 40, borderRadius: 6, background: 'var(--hj-beige-deep)' }}>
          {thumb ? <img src={thumb} alt="" className="w-full h-full" style={{ objectFit: 'cover' }} /> : <Play className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-3)' }} />}
        </div>
        <Input value={v.title || ''} onChange={e => onTitle(index, e.target.value)} placeholder={`Episode ${index + 1}`} className="h-9 flex-1" />
        {isVideo && (
          <button
            type="button"
            onClick={() => onToggleFree(index)}
            title="Make this whole video free to watch for anyone (even logged out)."
            className="flex items-center gap-1 flex-shrink-0"
            style={{ padding: '4px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600, border: '1px solid var(--hj-beige-line)', background: v.freePreview ? 'rgb(140 198 63 / 0.16)' : '#fff', color: v.freePreview ? '#5a8a2e' : 'var(--hj-beige-ink-3)', cursor: 'pointer' }}
          >
            <Eye className="w-3 h-3" /> {v.freePreview ? 'Free' : 'Set free'}
          </button>
        )}
        {v.duration ? <span className="tabular flex-shrink-0" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>{Math.round(v.duration / 60)}m</span> : null}
        {onEditVideo && isVideo && !!v.s3Key && (
          <button type="button" onClick={() => onEditVideo(index)} aria-label="Edit video" title="Edit video" className="flex-shrink-0" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--hj-beige-ink-3)' }}><Video className="w-4 h-4" /></button>
        )}
        <button type="button" onClick={() => onRemove(index)} aria-label="Remove" className="flex-shrink-0" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))' }}><Trash2 className="w-4 h-4" /></button>
      </div>

      {isSeries && (
        <div className="flex items-center flex-wrap gap-2" style={{ marginTop: 8, paddingLeft: draggable ? 44 : 0 }}>
          <Input
            value={v.logline || ''}
            onChange={e => onLogline(index, e.target.value)}
            placeholder="Logline (optional) — one line about this episode"
            className="h-8 flex-1"
            style={{ minWidth: 180, fontSize: 12.5 }}
          />
          {/* Free episodes ignore release timing — they're always watchable. */}
          {!v.freePreview && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Clock className="w-3.5 h-3.5" style={{ color: 'var(--hj-beige-ink-3)' }} />
              <select
                value={releaseMode}
                onChange={e => onRelease(index, { mode: e.target.value as 'immediate' | 'day' | 'date', value: e.target.value === 'day' ? (v.dripDay || 1) : (e.target.value === 'date' ? (v.availableDate || '') : undefined) })}
                className="h-8"
                style={{ borderRadius: 8, border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12, padding: '0 6px' }}
                title="When buyers can watch this episode."
              >
                <option value="immediate">Available now</option>
                <option value="day">Day N after purchase</option>
                <option value="date">On a date</option>
              </select>
              {releaseMode === 'day' && (
                <input
                  type="number" min={0} value={v.dripDay || 0}
                  onChange={e => onRelease(index, { mode: 'day', value: parseInt(e.target.value, 10) })}
                  className="tabular text-center h-8" style={{ width: 52, borderRadius: 8, border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontFamily: 'var(--hj-font-mono)' }}
                />
              )}
              {releaseMode === 'date' && (
                <input
                  type="date" value={v.availableDate || ''}
                  onChange={e => onRelease(index, { mode: 'date', value: e.target.value })}
                  className="h-8" style={{ borderRadius: 8, border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12, padding: '0 6px' }}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default VideoBuilderPage;
